import { Page, TextField, Modal } from '../components/forms';
import { useEffect, useState, useCallback } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  Checkbox,
  DataTable,
  EmptyState,
  Spinner,
  Badge,
} from '@shopify/polaris';
import { PlusIcon, DeleteIcon, EditIcon, ArrowUpIcon, ArrowDownIcon } from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';

interface ReturnReason {
  id: number;
  name: string;
  description: string | null;
  requires_photo: boolean;
  is_active: boolean;
}

export default function ReturnReasons() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canMove = hasPermission('returns.manage');
  const { fetchApi, error, loading } = useApi();
  const [reasons, setReasons] = useState<ReturnReason[]>([]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingReason, setEditingReason] = useState<ReturnReason | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingReason, setDeletingReason] = useState<ReturnReason | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formRequiresPhoto, setFormRequiresPhoto] = useState(false);
  const [formIsActive, setFormIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [moving, setMoving] = useState(false);

  const loadReasons = useCallback(async () => {
    const data = await fetchApi<{ reasons: ReturnReason[] }>('/returns/reasons');
    if (data) {
      setReasons(data.reasons || []);
    }
  }, [fetchApi]);

  useEffect(() => {
    setReasons([]);
    void loadReasons();
  }, [loadReasons]);

  const openCreateModal = () => {
    setEditingReason(null);
    setFormName('');
    setFormDescription('');
    setFormRequiresPhoto(false);
    setFormIsActive(true);
    setModalOpen(true);
  };

  const openEditModal = (reason: ReturnReason) => {
    setEditingReason(reason);
    setFormName(reason.name);
    setFormDescription(reason.description || '');
    setFormRequiresPhoto(Boolean(reason.requires_photo));
    setFormIsActive(Boolean(reason.is_active));
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) return;

    setSaving(true);
    try {
      const payload = {
        name: formName.trim(),
        description: formDescription.trim() || null,
        requires_photo: formRequiresPhoto,
        is_active: formIsActive,
      };

      if (editingReason) {
        const result = await fetchApi(`/returns/reasons/${editingReason.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        if (!result) return;
      } else {
        const result = await fetchApi('/returns/reasons', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        if (!result) return;
      }

      setModalOpen(false);
      loadReasons();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingReason) return;

    setSaving(true);
    try {
      const result = await fetchApi(`/returns/reasons/${deletingReason.id}`, {
        method: 'DELETE',
      });
      if (!result) return;
      setDeleteModalOpen(false);
      setDeletingReason(null);
      loadReasons();
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (index: number, direction: 'up' | 'down') => {
    const neighbor = reasons[index + (direction === 'up' ? -1 : 1)];
    if (!canMove || moving || !neighbor) return;
    setMoving(true);
    try {
      const saved = await fetchApi<{ reasons: ReturnReason[] }>(`/returns/reasons/${reasons[index].id}/move`, {
        method: 'POST',
        body: JSON.stringify({ direction, neighbor_id: neighbor.id }),
      });
      if (saved) setReasons(saved.reasons);
    } finally {
      setMoving(false);
    }
  };

  const tableRows = reasons.map((reason, index) => [
    <InlineStack gap="100" blockAlign="center" wrap={false}>
      <Button
        icon={ArrowUpIcon}
        size="micro"
        disabled={!canMove || moving || index === 0}
        onClick={() => handleReorder(index, 'up')}
        accessibilityLabel={`Move ${reason.name} up`}
      />
      <Button
        icon={ArrowDownIcon}
        size="micro"
        disabled={!canMove || moving || index === reasons.length - 1}
        onClick={() => handleReorder(index, 'down')}
        accessibilityLabel={`Move ${reason.name} down`}
      />
    </InlineStack>,
    <Text as="span" variant="bodyMd" fontWeight="semibold">{reason.name}</Text>,
    reason.description || '-',
    reason.requires_photo ? (
      <Badge tone="info">Required</Badge>
    ) : (
      <Badge>Optional</Badge>
    ),
    reason.is_active ? (
      <Badge tone="success">Active</Badge>
    ) : (
      <Badge tone="critical">Inactive</Badge>
    ),
    <InlineStack gap="100" blockAlign="center" wrap={false}>
      <Button
        icon={EditIcon}
        size="slim"
        onClick={() => openEditModal(reason)}
        accessibilityLabel="Edit"
      />
      <Button
        icon={DeleteIcon}
        size="slim"
        tone="critical"
        onClick={() => {
          setDeletingReason(reason);
          setDeleteModalOpen(true);
        }}
        accessibilityLabel="Retire"
      />
    </InlineStack>,
  ]);

  return (
    <Page
      title="Return Reasons"
      subtitle="Configure the reasons customers can select when requesting a return"
      backAction={{ content: 'Returns', onAction: () => navigate('/returns') }}
      primaryAction={{
        icon: PlusIcon, content: 'Add Reason',
        onAction: openCreateModal,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" action={{ content: 'Reload reasons', onAction: loadReasons }}>{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            {loading && reasons.length === 0 ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spinner />
              </div>
            ) : reasons.length === 0 ? (
              <EmptyState
                heading="No return reasons configured"
                action={{
                  icon: PlusIcon, content: 'Add Return Reason',
                  onAction: openCreateModal,
                }}
                image=""
              >
                <p>
                  Create return reasons that customers can select when requesting a return.
                  You can require photos for certain reasons like damaged items.
                </p>
              </EmptyState>
            ) : (
              <DataTable
                increasedTableDensity
                verticalAlign="middle"
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                headings={['Order', 'Name', 'Description', 'Photo', 'Status', 'Actions']}
                rows={tableRows}
              />
            )}
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">Tips</Text>
              <BlockStack gap="200">
                <Text as="p" variant="bodySm" tone="subdued">
                  • Return reasons appear to customers in the order shown above
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  • Requiring a photo helps verify claims of damage or defects
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  • Inactive reasons won't appear to customers but are preserved for historical records
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingReason ? 'Edit Return Reason' : 'Add Return Reason'}
        primaryAction={{
          icon: editingReason ? undefined : PlusIcon, content: editingReason ? 'Save Changes' : 'Create Reason',
          onAction: handleSave,
          loading: saving,
          disabled: !formName.trim(),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          {error && <Banner tone="critical">{error}</Banner>}
          <BlockStack gap="400">
            <TextField
              label="Reason Name"
              requiredIndicator value={formName}
              onChange={setFormName}
              autoComplete="off"
              placeholder="e.g., Wrong item received"
              helpText="This is what customers will see when selecting a return reason"
            />

            <TextField
              label="Description"
              value={formDescription}
              onChange={setFormDescription}
              autoComplete="off"
              multiline={3}
              placeholder="Optional description or instructions for this reason"
              helpText="Additional context shown to customers (optional)"
            />

            <Checkbox
              label="Require photo upload"
              checked={formRequiresPhoto}
              onChange={setFormRequiresPhoto}
              helpText="Customers must upload a photo when selecting this reason"
            />

            <Checkbox
              label="Active"
              checked={formIsActive}
              onChange={setFormIsActive}
              helpText="Inactive reasons won't be shown to customers"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingReason(null);
        }}
        title="Retire Return Reason"
        primaryAction={{
          content: 'Retire',
          onAction: handleDelete,
          loading: saving,
          destructive: true,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setDeleteModalOpen(false);
              setDeletingReason(null);
            },
          },
        ]}
      >
        <Modal.Section>
          {error && <Banner tone="critical">{error}</Banner>}
          <Text as="p">
            Retire the return reason "{deletingReason?.name}"?
          </Text>
          <Text as="p" tone="subdued">
            Existing returns using this reason will keep their historical data.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
